/**
 * Libellés français du parc machines.
 *
 * Comme pour les rôles de tiers, la traduction des codes de l'API vit en un
 * seul endroit : la liste, la fiche et le rapport d'import doivent dire la même
 * chose du même état.
 */
import type { AssetAttribute, AssetStatus, Completeness } from '../api/client';

export const STATUT_MACHINE: Record<AssetStatus, { texte: string; classe: string }> = {
  IN_SERVICE: { texte: 'En service', classe: 'actif' },
  IN_MAINTENANCE: { texte: 'En intervention', classe: 'attente' },
  OUT_OF_SERVICE: { texte: 'Arrêtée', classe: 'suspendu' },
  IN_STOCK: { texte: 'En stock', classe: 'suspendu' },
  DECOMMISSIONED: { texte: 'Déposée', classe: 'revoque' },
};

export const STATUTS_MACHINE = Object.keys(STATUT_MACHINE) as AssetStatus[];

/**
 * Les trois états de complétude du CDC §8.2.
 *
 * « Non communiqué » et « À renseigner » se ressemblent à l'œil mais ne
 * veulent pas dire la même chose : le premier a été tranché par quelqu'un, le
 * second attend encore. La couleur les distingue.
 */
export const COMPLETUDE: Record<Completeness, { texte: string; classe: string }> = {
  PROVIDED: { texte: 'Renseigné', classe: 'actif' },
  MISSING: { texte: 'À renseigner', classe: 'attente' },
  UNAVAILABLE: { texte: 'Non communicable', classe: 'suspendu' },
};

/**
 * Ordre d'affichage des caractéristiques dans la carte Identification de la
 * fiche machine (demande de Benjamin, 2026-10-01), par rapport au champ fixe
 * « Mise en service ». Les codes sont ceux du jeu de départ proposé par
 * `/champs-techniques` (`DEFINITIONS_DE_DEPART` côté API) — un champ
 * personnalisé qu'un tenant ajoute en plus ne figure dans aucune des deux
 * listes : il s'affiche quand même, à la toute fin de la carte, jamais perdu.
 */
export const CARACTERISTIQUES_AVANT_MISE_EN_SERVICE = [
  'puissance_kw',
  'pression_service_bar',
  'tension_v',
  'annee_fabrication',
];

export const CARACTERISTIQUES_APRES_MISE_EN_SERVICE = [
  'debit_m3h',
  'fluide_frigorigene',
  'charge_fluide_kg',
  'sous_garantie',
  'derniere_verification',
];

/**
 * Répartit les caractéristiques d'une machine selon l'ordre ci-dessus.
 *
 * Une seule fonction pour la fiche ET le formulaire « Corriger » : l'ordre que
 * Benjamin a choisi pour la lecture doit être celui de la saisie (écart relevé le
 * 2026-10-05 : « Corriger » suivait l'ordre brut de l'API). `restants` porte les
 * champs personnalisés, absents des deux listes — jamais perdus.
 */
export function ordonnerCaracteristiques(attributs: readonly AssetAttribute[]): {
  avant: AssetAttribute[];
  apres: AssetAttribute[];
  restants: AssetAttribute[];
} {
  const parCode = (codes: readonly string[]) =>
    codes
      .map((code) => attributs.find((a) => a.code === code))
      .filter((a): a is AssetAttribute => a !== undefined);
  const connus = new Set([
    ...CARACTERISTIQUES_AVANT_MISE_EN_SERVICE,
    ...CARACTERISTIQUES_APRES_MISE_EN_SERVICE,
  ]);
  return {
    avant: parCode(CARACTERISTIQUES_AVANT_MISE_EN_SERVICE),
    apres: parCode(CARACTERISTIQUES_APRES_MISE_EN_SERVICE),
    restants: attributs.filter((a) => !connus.has(a.code)),
  };
}
